import * as yup from 'yup';

import { Button, Input } from '.';
import { Card, CardBody, CardFooter, Form, FormGroup, Label } from 'reactstrap';
import { CitationDetailDto, CitationDto, ProfessionStandardsDesignationDto } from '../common/Types';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';

import moment from 'moment';
import { professionService } from '../services/ProfessionAdministration';
import { recommendationService } from '../services/RecommendationService';
import { toast } from 'react-toastify';

interface CitationFormProps {
    citation: CitationDto;
    professionId: number;
    onCancel: () => void;
    onSave: () => void;
}

const CitationForm = (props: CitationFormProps) => {
    const { citation, professionId, onCancel, onSave } = props;

    const [designations, setDesignations] = useState<ProfessionStandardsDesignationDto[]>();

    const citationSchema = yup
        .object({
            citationId: yup.number().required().default(citation.citationId),
            letterId: yup.number().notRequired().nullable(),
            standardsDesignationId: yup.number().required().min(1, 'Please select a standard designation'),
            designationNumber: yup.string().notRequired().nullable(),
            designationTitle: yup.string().notRequired().nullable(),
            designationDescription: yup.string().notRequired().nullable(),
            programId: yup.number().nullable().default(citation.programId),
            recommendationId: yup.number().required().default(citation.recommendationId),
            rationale: yup.string().notRequired().nullable(),
            suggestedDocumentation: yup.string().notRequired().nullable(),
            createdBy: yup.string().notRequired().nullable().default(citation.createdBy),
            dateCreated: yup.date().notRequired().nullable().default(moment(citation.dateCreated).toDate()),
            lastEdited: yup.date().notRequired().nullable().default(moment(citation.lastEdited).toDate()),
            editedBy: yup.string().notRequired().nullable().default(citation.editedBy),
            isDeleted: yup.boolean().required().default(false),
            editedByString: yup.string().notRequired().nullable().default(citation.editedByString),
            createdByString: yup.string().notRequired().nullable().default(citation.createdByString),
            detail: yup
                .array()
                .of(
                    yup
                        .object<CitationDetailDto>({
                            citationDetailId: yup.number().required().default(0),
                            citationId: yup.number().required().default(citation.citationId),
                            subStandardId: yup.number().notRequired().nullable(),
                            rationaleId: yup
                                .number()
                                .notRequired()
                                .nullable()
                                .transform((cv, ov) => {
                                    return ov === '' ? undefined : +cv;
                                }),
                            subStandardDescription: yup.string().notRequired().nullable(),
                        })
                        .defined(),
                )
                .default([]),
        })
        .defined();

    const {
        register,
        handleSubmit,
        watch,
        control,
        formState: { errors },
    } = useForm<CitationDto>({
        resolver: yupResolver(citationSchema as any),
        defaultValues: citation,
    });
    const { append, remove, fields } = useFieldArray({
        name: 'detail',
        control: control,
    });

    useEffect(() => {
        const loadDesignations = () => {
            if (professionId) {
                professionService
                    .getProfessionStandardDesignations(professionId)
                    .then((desgs) => {
                        setDesignations(desgs);
                    })
                    .catch(() => {
                        toast.error('Error loading standard designations');
                    });
            }
        };

        loadDesignations();
    }, [professionId]);

    const submitForm = (values: CitationDto) => {
        const toastId = toast.info('Saving citation...');

        recommendationService
            .saveCitationAndDetail(values)
            .then((_result) => {
                toast.update(toastId, {
                    render: 'Citation saved successfully',
                    type: 'success',
                });
                onSave();
            })
            .catch(() => {
                toast.update(toastId, {
                    render: 'Error saving citation',
                    type: 'error',
                });
            });
    };

    const standardsDesignationId = +(watch('standardsDesignationId') ?? -1);
    const selectedDesignation = designations?.find((d) => d.standardsDesignationId === standardsDesignationId);

    const subStandardChanged = (subStandardId: number, isChecked: boolean) => {
        const existingField = fields?.find((f) => f.subStandardId && +f.subStandardId === +subStandardId);

        if (isChecked && !!!existingField) {
            append({
                citationDetailId: 0,
                citationId: citation.citationId,
                subStandardId: subStandardId,
                rationaleId: undefined,
                subStandardDescription: undefined,
            });
        }

        if (!isChecked && !!existingField) {
            const index = fields.indexOf(existingField);
            remove(index);
        }
    };

    return (
        <Form noValidate onSubmit={handleSubmit(submitForm)}>
            {fields &&
                fields.map((f, i) => (
                    <div key={f.id}>
                        <input type="hidden" {...register(`detail.${i}.citationDetailId`)} />
                        <input type="hidden" {...register(`detail.${i}.citationId`)} />
                        <input type="hidden" {...register(`detail.${i}.subStandardId`)} />
                        <input
                            type={'hidden'}
                            {...register(`detail.${i}.rationaleId`)}
                            defaultValue={f.rationaleId || ''}
                        />
                    </div>
                ))}
            <Card className={'mb-0'}>
                <CardBody>
                    <h4 className={'card-title'}>{`${citation.citationId > 0 ? 'Edit' : 'New'} Citation`}</h4>

                    <FormGroup>
                        <Label>{`Standard Designation`}</Label>
                        {designations && (
                            <Controller
                                name="standardsDesignationId"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        type={'select'}
                                        {...field}
                                        onChange={(e) => field.onChange(+e.target.value)}
                                    >
                                        <option value={-1}>{`Select...`}</option>
                                        {designations.map((d) => (
                                            <option key={d.standardsDesignationId} value={d.standardsDesignationId}>
                                                {`${d.designationNumber} - ${d.designationTitle}`}
                                            </option>
                                        ))}
                                    </Input>
                                )}
                            />
                        )}
                        {errors?.standardsDesignationId?.message && (
                            <span className={'text-danger'}>{errors.standardsDesignationId.message}</span>
                        )}
                    </FormGroup>
                    {selectedDesignation && (
                        <FormGroup>
                            <p>{selectedDesignation.designationDescription}</p>
                        </FormGroup>
                    )}
                    {selectedDesignation?.professionStandardsDesignationSubStandard && (
                        <>
                            <FormGroup>
                                {selectedDesignation.professionStandardsDesignationSubStandard
                                    .filter((ss) => ss.isDeleted !== true)
                                    .map((ss, i) => (
                                        <div key={ss.subStandardId} className={'custom-control custom-checkbox mb-2'}>
                                            <Input
                                                type={'checkbox'}
                                                className={'custom-control-input'}
                                                id={`substandard[${i}]`}
                                                checked={
                                                    !!fields?.find(
                                                        (d) =>
                                                            d.subStandardId && +d.subStandardId === +ss.subStandardId,
                                                    )
                                                }
                                                onChange={(e) => subStandardChanged(ss.subStandardId, e.target.checked)}
                                            />
                                            <Label
                                                className={'custom-control-label font-weight-normal'}
                                                for={`substandard[${i}]`}
                                            >
                                                {ss.description}
                                            </Label>
                                        </div>
                                    ))}
                                {errors?.detail?.message && (
                                    <span className={'text-danger'}>{errors.detail.message}</span>
                                )}
                            </FormGroup>

                            <FormGroup>
                                <Label>{`Rationale`}</Label>
                                <Controller
                                    name="rationale"
                                    control={control}
                                    render={({ field }) => <Input {...field} type="textarea" rows={10} />}
                                />
                                {errors?.rationale?.message && (
                                    <span className={'text-danger'}>{errors.rationale.message}</span>
                                )}
                            </FormGroup>

                            <FormGroup>
                                <Label>{`Requested Documentation`}</Label>
                                <Controller
                                    name="suggestedDocumentation"
                                    control={control}
                                    render={({ field }) => <Input {...field} type="textarea" rows={10} />}
                                />
                                {errors?.suggestedDocumentation?.message && (
                                    <span className={'text-danger'}>{errors.suggestedDocumentation.message}</span>
                                )}
                            </FormGroup>
                        </>
                    )}
                </CardBody>
                <CardFooter>
                    <Button type={'submit'} color={'primary'}>{`Submit`}</Button>
                    <Button
                        type={'button'}
                        color={'secondary'}
                        className={'ml-3'}
                        onClick={() => onCancel()}
                    >{`Cancel`}</Button>
                </CardFooter>
            </Card>
        </Form>
    );
};

export default CitationForm;
